import { Link } from "react-router-dom";
import { FINDING_STATUSES, SEVERITIES, type SchoolSummary } from "../../api/client";
import { ErrorMessage, Notice, PageSkeleton } from "../../components/ui";
import { t } from "../../i18n/t";
import {
  formatDateTime,
  formatNumber,
  levelLabel,
  severityLabel,
  statusLabel,
} from "../../lib/format";
import { routes } from "../../lib/routes";
import { usePageTitle } from "../../lib/usePageTitle";
import { useKommune } from "./KommuneContext";

function Addresses({ school }: { school: SchoolSummary }) {
  const entries = Object.entries(school.addresses);
  return (
    <>
      {entries.length <= 1 ? (
        <span>{formatNumber(entries[0]?.[1] ?? 0)}</span>
      ) : (
        <ul className="plain-list">
          {entries.map(([level, n]) => (
            <li key={level}>
              {levelLabel(level)}: {formatNumber(n)}
            </li>
          ))}
        </ul>
      )}
      {school.addresses_within_2km > 0 && (
        <span className="cell-note">
          {t("dashboard.within2km", { count: formatNumber(school.addresses_within_2km) })}
        </span>
      )}
      {school.exposure && school.exposure.addresses > 0 && (
        <span className="cell-note">
          {t("dashboard.withHigh", {
            withHigh: formatNumber(school.exposure.with_high),
            addresses: formatNumber(school.exposure.addresses),
            pct: Math.round((100 * school.exposure.with_high) / school.exposure.addresses),
          })}
        </span>
      )}
    </>
  );
}

export function DashboardPage() {
  const { orgId, nr, navn, overview, overviewError, overviewLoading, reloadOverview } =
    useKommune();
  usePageTitle(navn);

  if (overviewError) return <ErrorMessage error={overviewError} onRetry={reloadOverview} />;
  if (!overview) return <PageSkeleton label={t("app.loading")} />;

  const { run, totals, schools, warnings } = overview;
  const totalFindings = SEVERITIES.reduce((sum, s) => sum + (totals[s] ?? 0), 0);

  return (
    <div className="page" aria-busy={overviewLoading}>
      <header className="page-header">
        <p className="eyebrow">{t("dashboard.kommuneNummer", { nummer: overview.nummer })}</p>
        <h1>{overview.navn || navn}</h1>
        {run && (
          <p className="muted">
            {t("dashboard.runInfo", {
              snapshot: formatDateTime(run.snapshot_at),
              finished: formatDateTime(run.finished_at),
            })}
          </p>
        )}
        {run && (overview.criteria_title || run.criteria_version) && (
          <p className="criteria-line">
            {t("dashboard.criteria", {
              title: overview.criteria_title ?? run.criteria_version ?? "",
              version: run.criteria_version ?? t("common.unknown"),
            })}
            {overview.criteria_status && (
              <span className="badge badge-draft">{overview.criteria_status}</span>
            )}
          </p>
        )}
      </header>

      {!run && (
        <Notice kind="info" title={t("dashboard.noRunTitle")}>
          <p>{t("dashboard.noRunBody", { navn: overview.navn || navn })}</p>
        </Notice>
      )}

      {run && (
        <Notice kind="info" title={t("dashboard.disclaimerTitle")} role="note">
          <p>{t("dashboard.disclaimer")}</p>
        </Notice>
      )}

      {warnings.length > 0 && (
        <Notice kind="warning" title={t("dashboard.warningsTitle")} role="note">
          <ul>
            {warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </Notice>
      )}

      {overview.latest_change_id && (
        <Notice kind="info">
          <p>
            {t("dashboard.latestChange")}{" "}
            <Link to={routes.change(orgId, nr, overview.latest_change_id)}>
              {t("dashboard.latestChangeLink")}
            </Link>
          </p>
        </Notice>
      )}

      {run && (
        <>
          <section className="section" aria-labelledby="totals-heading">
            <h2 id="totals-heading">{t("dashboard.totalsTitle")}</h2>
            <ul className="stat-tiles">
              {SEVERITIES.map((s) => (
                <li key={s} className={`stat-tile stat-${s}`}>
                  <Link to={routes.tiltak(orgId, nr, { alvorlighet: s })}>
                    <span className="stat-value">{formatNumber(totals[s] ?? 0)}</span>{" "}
                    <span className="stat-label">{severityLabel(s)}</span>
                  </Link>
                </li>
              ))}
            </ul>
            <p>
              {t("dashboard.totalFindings", { count: formatNumber(totalFindings) })}{" "}
              <span className="muted">
                {"("}
                {FINDING_STATUSES.map(
                  (s) => `${statusLabel(s)}: ${formatNumber(overview.status_counts[s] ?? 0)}`,
                ).join(", ")}
                {")"}
              </span>
            </p>
            <p>
              <Link to={routes.tiltak(orgId, nr)} className="btn btn-primary">
                {t("dashboard.openTiltak")}
              </Link>
            </p>
          </section>

          <section className="section" aria-labelledby="schools-heading">
            <h2 id="schools-heading">{t("dashboard.schoolsTitle")}</h2>
            {schools.length === 0 ? (
              <p>{t("dashboard.noSchools")}</p>
            ) : (
              <div className="table-wrap">
                <table className="data-table">
                  <caption className="visually-hidden">
                    {t("dashboard.schoolsCaption", { navn: overview.navn || navn })}
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">{t("dashboard.colSchool")}</th>
                      <th scope="col">{t("dashboard.colTrinn")}</th>
                      <th scope="col">{t("dashboard.colAddresses")}</th>
                      <th scope="col" className="num">
                        {t("dashboard.colHigh")}
                      </th>
                      <th scope="col" className="num">
                        {t("dashboard.colMedium")}
                      </th>
                      <th scope="col" className="num">
                        {t("dashboard.colLow")}
                      </th>
                      <th scope="col" className="num">
                        {t("dashboard.colOpen")}
                      </th>
                      <th scope="col">{t("dashboard.colLinks")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schools.map((s) => (
                      <tr key={s.school_id}>
                        <th scope="row">
                          <Link to={routes.school(orgId, nr, s.school_id)}>{s.name}</Link>
                          {!s.snapped && (
                            <span className="cell-note warning-text">
                              {t("dashboard.notSnapped")}
                            </span>
                          )}
                        </th>
                        <td>{t("dashboard.trinn", { from: s.trinn_from, to: s.trinn_to })}</td>
                        <td>
                          <Addresses school={s} />
                        </td>
                        {SEVERITIES.map((sev) => (
                          <td key={sev} className="num">
                            <span className={`count sev-text-${sev}`}>
                              {formatNumber(s.findings_by_severity[sev] ?? 0)}
                            </span>
                          </td>
                        ))}
                        <td className="num">{formatNumber(s.open_findings)}</td>
                        <td>
                          <span className="link-group">
                            <Link
                              to={routes.school(orgId, nr, s.school_id)}
                              aria-label={t("dashboard.mapLinkLabel", { school: s.name })}
                            >
                              {t("dashboard.mapLink")}
                            </Link>
                            <Link
                              to={routes.tiltak(orgId, nr, { skole: s.school_id })}
                              aria-label={t("dashboard.tiltakLinkLabel", { school: s.name })}
                            >
                              {t("dashboard.tiltakLink")}
                            </Link>
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
